import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AuthShell, IconInput } from "./AuthShell";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, Mail, Lock, ShieldCheck } from "lucide-react";
import { isValidEmail } from "../lib/validate";

export default function Login() {
  const { login, verifyOtp, resendOtp, resendActivation, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  // MFA step
  const [step, setStep] = useState("creds"); // creds | otp
  const [challengeId, setChallengeId] = useState("");
  const [otpDest, setOtpDest] = useState({ channel: "email", dest: "" });
  const [code, setCode] = useState("");
  const [idleNotice, setIdleNotice] = useState("");

  useEffect(() => {
    if (user) navigate(user.role === "Visitor" ? "/welcome" : "/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("logout_reason") === "idle") {
        setIdleNotice("You were signed out due to inactivity. Please sign in again.");
        sessionStorage.removeItem("logout_reason");
      }
    } catch {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setResendMsg("");
    if (!isValidEmail(email)) { setError("Enter a valid email address."); return; }
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    if (res.mfa) { setChallengeId(res.challengeId); setOtpDest({ channel: res.channel || "email", dest: res.dest || email }); setStep("otp"); setPassword(""); return; }
    navigate(res.user?.role === "Visitor" ? "/welcome" : "/dashboard", { replace: true });
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setResendMsg("");
    if (!/^\d{6}$/.test(code.trim())) { setError("Enter the 6-digit code we sent you."); return; }
    setLoading(true);
    const res = await verifyOtp(challengeId, code.trim());
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    navigate(res.user?.role === "Visitor" ? "/welcome" : "/dashboard", { replace: true });
  };

  const doResendOtp = async () => {
    setError("");
    const res = await resendOtp(challengeId);
    if (res.ok && res.channel) setOtpDest({ channel: res.channel, dest: res.dest || otpDest.dest });
    setResendMsg(res.ok ? (res.message || "A new code has been sent.") : (res.error || "Could not resend."));
  };

  const needsActivation = error.toLowerCase().includes("activate");
  const doResendActivation = async () => {
    const res = await resendActivation(email);
    setResendMsg(res.ok ? (res.message || "Activation email sent.") : (res.error || "Could not resend."));
  };

  if (step === "otp") {
    return (
      <AuthShell>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8" data-testid="otp-step">
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-[#1E3A8A]">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4 text-center">Two-step verification</h2>
          <p className="text-sm text-slate-500 mt-1 text-center" data-testid="otp-channel-note">
            {otpDest.channel === "sms"
              ? <>We texted a 6-digit code to <b>{otpDest.dest}</b>. Enter it below to finish signing in.</>
              : <>We emailed a 6-digit code to <b>{otpDest.dest || email}</b>. Enter it below to finish signing in.</>}
          </p>

          {error && <div data-testid="otp-error" className="mt-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">{error}</div>}
          {resendMsg && <div data-testid="otp-resend-msg" className="mt-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2">{resendMsg}</div>}

          <form onSubmit={verify} noValidate className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="otp-code">Verification code</Label>
              <IconInput icon={ShieldCheck} id="otp-code" data-testid="otp-code" inputMode="numeric" maxLength={6}
                autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456" className="tracking-[0.4em] text-lg" />
            </div>
            <Button type="submit" data-testid="otp-verify" disabled={loading}
              className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] h-11">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & Sign In
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <button type="button" data-testid="otp-resend" onClick={doResendOtp} className="text-blue-700 font-semibold hover:underline">Resend code</button>
            <button type="button" data-testid="otp-back" onClick={() => { setStep("creds"); setCode(""); setError(""); setResendMsg(""); }} className="text-slate-500 hover:underline">Use a different account</button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h2>
        <p className="text-sm text-slate-500 mt-1">Sign in to your STEMS account.</p>

        {idleNotice && (
          <div data-testid="idle-notice" className="mt-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            {idleNotice}
          </div>
        )}
        {error && (
          <div data-testid="login-error" className="mt-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
            {error}
            {needsActivation && (
              <button type="button" data-testid="resend-activation" onClick={doResendActivation}
                className="block mt-1 font-semibold text-blue-700 hover:underline">
                Resend activation email
              </button>
            )}
          </div>
        )}
        {resendMsg && (
          <div data-testid="resend-message" className="mt-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2">
            {resendMsg}
          </div>
        )}

        <form onSubmit={submit} noValidate className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <IconInput icon={Mail} id="email" data-testid="login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@silastech.com" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-blue-700 hover:underline">
                Forgot password?
              </Link>
            </div>
            <IconInput icon={Lock} id="password" data-testid="login-password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <Button type="submit" data-testid="login-submit" disabled={loading}
            className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] h-11">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign In
          </Button>
        </form>

        <p className="mt-6 text-sm text-center text-slate-500">
          Don’t have an account?{" "}
          <Link to="/register" data-testid="register-link" className="text-blue-700 font-semibold hover:underline">
            Create an Account
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
